import { useState, useEffect, useMemo } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Loader2,
  Plus,
  Pencil,
  Check,
  ChevronsUpDown,
  Info,
  Lock,
} from "lucide-react";
import { useAuthStore } from "@/stores/authStore";
import { nutritionAPI, type FoodServingPayload } from "@/lib/nutrition-api";
import {
  foodVersionAPI,
  isFoodBlockerError,
  isFoodConflictError,
  isFoodPreconditionRequiredError,
  type VersionedFoodItem,
} from "@/lib/food-version-api";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { useToast } from "@/components/ui/use-toast";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import { cn } from "@/lib/utils";
import { queryKeys, invalidateQueries } from "@/lib/query-keys";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { RequiredMark } from "@/components/settings/nutrition-shared";
import {
  buildDescription,
  previewMultiplierFor,
  SERVING_DESCRIPTION_HELPER,
  SERVING_DESCRIPTION_PLACEHOLDER,
  SERVING_ML_WEIGHT_HELPER,
  SERVING_MULTIPLIER_HELPER,
  SERVING_MULTIPLIER_PLACEHOLDER,
} from "@/components/settings/serving-form-shared";
import {
  FOOD_NUTRIENT_BASIS_LABELS,
  SERVING_MULTIPLIER_MIN,
  validateServingMultiplier,
  validateServingWeightG,
  type FoodNutrientBasis,
} from "@/lib/nutrition-form-utils";

// Which field carries the truth depends on the parent food's nutrient_basis:
//
//   "100G" — weight_g is required; the multiplier is derived from it
//            server-side and a sent value is ignored (columbus#404). We do not
//            compute weight / 100 here: duplicating the server's arithmetic to
//            satisfy a backend contract we have not confirmed is how the two
//            drifted apart in the first place.
//   "SERV" — the multiplier IS the fact and has no server default; weight_g is
//            optional, because a capsule has no meaningful gram weight.
//
// Never pre-fill the SERV multiplier. That pre-fill is half the root cause of
// the original drift, and omitting the field returns 400 rather than guessing.
const foodServingBaseSchema = z.object({
  food_item: z.number().min(1, "Food item is required"),
  serving_unit: z.number().min(1, "Serving unit is required"),
  weight_g: z.string().optional(),
  multiplier: z.string().optional(),
  // Which unit the serving's auto-filled name shows the portion in. Display only:
  // it never converts anything, the weight is always typed and stored in grams,
  // and this never leaves the form.
  weight_unit: z.enum(["g", "ml"]).optional(),
});

type FoodServingFormValues = z.infer<typeof foodServingBaseSchema>;

// The weight and multiplier rules are shared with the per-serving supplement
// form on Add Food Entry, so the same food validates the same way whether its
// first serving is created with it or added here afterwards.
const buildFoodServingSchema = (nutrientBasis: FoodNutrientBasis) =>
  foodServingBaseSchema.superRefine((values, ctx) => {
    const isPerServing = nutrientBasis === "SERV";

    const weightError = validateServingWeightG(
      values.weight_g ?? "",
      !isPerServing
    );
    if (weightError) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["weight_g"],
        message: weightError,
      });
    }

    if (!isPerServing) return;

    const multiplierError = validateServingMultiplier(values.multiplier ?? "");
    if (multiplierError) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["multiplier"],
        message: multiplierError,
      });
    }
  });

interface FoodServingsCardProps {
  foodItemId: number;
  foodItemName: string;
  /**
   * The parent food's nutrient_basis. Defaults to "100G" — every food in the
   * catalog today is per-100 g, and only supplements can be anything else.
   */
  nutrientBasis?: FoodNutrientBasis;
  /**
   * The food's server-owned `capabilities.can_edit`. A serving write stages a
   * draft onto the parent food, so the food's lock is the serving's lock: the
   * API answers 403 REFERENCE_SOURCE_LOCKED on a reference-source food and 404
   * on a retired one. Defaults to true so a payload without capabilities does
   * not wrongly lock the table — same default as ViewFood.
   */
  canEdit?: boolean;
  /**
   * Why editing is off, in the user's words. Shown instead of the actions when
   * `canEdit` is false. Without it the table simply goes read-only.
   */
  lockedReason?: string;
}

// DRF answers a rejected write with {"multiplier": ["This field is required."]}.
// The shared getApiErrorMessage flattens that to the bare sentence, which on a
// form with two optional-looking numeric fields says nothing useful — so name
// the field here.
const SERVING_FIELD_LABELS: Record<string, string> = {
  food_item: "Food item",
  serving_unit: "Serving Unit",
  description: "Description",
  weight_g: "Weight (g)",
  multiplier: "Multiplier",
};

const describeServingError = (error: any, fallback: string): string => {
  const body = error?.response?.data;
  // The API wraps field errors as { error, message, code, detail, errors }; read
  // the field errors, not the wrapper.
  const data =
    body?.errors && typeof body.errors === "object" ? body.errors : body;
  if (data && typeof data === "object" && !Array.isArray(data)) {
    const parts = Object.entries(data)
      .map(([field, detail]) => {
        const text = Array.isArray(detail)
          ? detail.filter(Boolean).join(" ")
          : String(detail ?? "");
        if (!text.trim()) return null;
        if (field === "detail" || field === "non_field_errors") return text;
        return `${SERVING_FIELD_LABELS[field] ?? field}: ${text}`;
      })
      .filter(Boolean);
    if (parts.length) return parts.join(" · ");
  }
  return error?.message || fallback;
};

/**
 * Why the pencil is closed on a saved serving.
 *
 * A serving is not a label, it is the arithmetic behind advice already given:
 * "1 cup" resolved to 143 g when a plan was written, and editing the row would
 * silently restate that plan as something the patient was never told. So a saved
 * serving is not edited: an admin can switch it off, and past entries stay pinned
 * to the version they were advised on.
 */
const SERVING_EDIT_CLOSED_REASON =
  "A saved serving can't be changed — plans already advised on it would change too. An admin can switch it off.";

// Switching a serving off or on takes it out of, or back into, use for new
// entries, and a meal plan may already use it, so it is an admin decision. The
// server is what enforces it; the console only shows the switch to admins.
const SERVING_SWITCH_DEFAULT_REASON =
  "This is the default serving. Make another serving the default before switching it off.";
const SERVING_SWITCH_DRAFT_REASON =
  "This food has an unpublished draft. Publish or discard it before switching a serving.";

// The API wraps errors as { error, message, code, detail }. Read the readable
// text out of `detail` (a string, or field -> messages), then `message`.
const switchErrorMessage = (error: any, fallback: string): string => {
  const data = error?.response?.data;
  const flatten = (value: unknown): string[] =>
    Array.isArray(value)
      ? value.flatMap(flatten)
      : value && typeof value === "object"
        ? Object.values(value).flatMap(flatten)
        : typeof value === "string"
          ? [value]
          : [];
  const parts = flatten(data?.detail);
  if (parts.length) return parts.join(" ");
  if (typeof data?.message === "string" && data.message) return data.message;
  return error?.message || fallback;
};

// Human labels for the coarse `draft_changed` categories (FV8), used to warn
// that publishing releases more than the servings edited on this screen.
const CHANGED_PART_LABELS: Record<string, string> = {
  profile: "profile fields",
  nutrient_values: "nutrient values",
  servings: "servings",
  tags: "tags",
};

const formatList = (items: string[]): string => {
  if (items.length <= 1) return items[0] ?? "";
  if (items.length === 2) return `${items[0]} and ${items[1]}`;
  return `${items.slice(0, -1).join(", ")}, and ${items[items.length - 1]}`;
};

// The staged-draft preview (`draft_servings`) carries the unit only nested as
// `serving_unit`, without the flat name/code the live list sends — read either,
// or every staged row shows a blank unit and opens for edit with none chosen.
const servingUnitName = (serving: {
  serving_unit_name?: string;
  serving_unit?: { name?: string } | null;
}) => serving.serving_unit_name || serving.serving_unit?.name || "";
const servingUnitCode = (serving: {
  serving_unit_code?: number;
  serving_unit?: { code?: number } | null;
}) => serving.serving_unit_code ?? serving.serving_unit?.code;

export const FoodServingsCard = ({
  foodItemId,
  foodItemName,
  nutrientBasis = "100G",
  canEdit = true,
  lockedReason,
}: FoodServingsCardProps) => {
  const isPerServing = nutrientBasis === "SERV";
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [isCreating, setIsCreating] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [servingUnitSearch, setServingUnitSearch] = useState("");
  const [servingUnitOpen, setServingUnitOpen] = useState(false);
  const [modifiedFields, setModifiedFields] = useState<Set<string>>(new Set());

  // Care managers may view servings but not change the serving unit — the unit
  // combobox is locked for that role.
  const isCareManager = useAuthStore((s) => s.isCareManager());
  // Switching a serving off or on is admin-only. Only an admin asks for, and
  // sees, the servings that are switched off.
  const isStaffAdmin = useAuthStore((s) => s.isStaffAdmin());
  const servingListParams = {
    food_item: foodItemId,
    ...(isStaffAdmin ? { include_inactive: true } : {}),
  };

  const {
    data: foodServingsData,
    isLoading: isLoadingServings,
    error: servingsError,
  } = useQuery({
    queryKey: queryKeys.nutrition.foodServings.list(servingListParams),
    queryFn: () => nutritionAPI.foodServings.list(servingListParams),
    retry: false,
    enabled: !!foodItemId,
  });

  const { data: servingUnits, isLoading: isLoadingServingUnits } = useQuery({
    queryKey: queryKeys.nutrition.servingUnits.list({ ordering: "name" }),
    queryFn: () => nutritionAPI.servingUnits.list({ ordering: "name" }),
    retry: false,
  });

  // The If-Match token for every staged serving write is the OWNING FOOD's
  // `version`, not a serving-specific value (contract §3). Read it from the food
  // payload — the same GET already carries the draft-state fields we surface.
  const { data: food } = useQuery({
    queryKey: queryKeys.nutrition.foodItems.detail(foodItemId),
    queryFn: () =>
      nutritionAPI.foodItems.get(foodItemId) as Promise<VersionedFoodItem>,
    retry: false,
    enabled: !!foodItemId,
  });

  const foodVersion = food?.version ?? null;
  const hasStagedDraft = food?.has_unpublished_changes ?? false;
  const draftEditor = food?.draft_updated_by ?? null;
  const draftTarget = food?.draft_target_version_number ?? null;
  // FV8 payload additions — absent until #410 deploys, so both degrade to the
  // pre-FV8 fallbacks (live table + generic banner) on today's backend.
  const draftServings = food?.draft_servings ?? null;
  const otherStagedChanges = (food?.draft_changed ?? [])
    .filter((part) => part !== "servings")
    .map((part) => CHANGED_PART_LABELS[part] ?? part);

  const servingSchema = useMemo(
    () => buildFoodServingSchema(nutrientBasis),
    [nutrientBasis]
  );

  const form = useForm<FoodServingFormValues>({
    resolver: zodResolver(servingSchema),
    defaultValues: {
      food_item: foodItemId,
      serving_unit: undefined,
      weight_g: "",
      multiplier: "",
      weight_unit: "g",
    },
    mode: "onSubmit",
  });

  useEffect(() => {
    form.setValue("food_item", foodItemId);
  }, [foodItemId, form]);

  // Every serving write now stages into the owning food's draft, carrying the
  // food's `version` as If-Match and returning the food's own payload (§3).
  const applyStagedFood = (updatedFood: VersionedFoodItem) => {
    // Seed the fresh version immediately so a rapid second edit sends the right
    // If-Match without waiting on a refetch.
    queryClient.setQueryData(
      queryKeys.nutrition.foodItems.detail(foodItemId),
      updatedFood
    );
    // Live serving rows only move when a clean food auto-published; when the
    // edit merely staged, the list is unchanged and this refetch is cheap.
    queryClient.invalidateQueries(
      invalidateQueries.nutritionFoodServingList({ food_item: foodItemId })
    );
    // Keep the food list (draft pills, version badges) in step.
    queryClient.invalidateQueries(invalidateQueries.nutritionFoodItems());
  };

  const notifyStaged = (updatedFood: VersionedFoodItem, verb: string) => {
    if (updatedFood.has_unpublished_changes) {
      // Joined an in-progress draft — it publishes with that draft, not now.
      const target = updatedFood.draft_target_version_number;
      toast({
        title: `Serving ${verb} — staged in the draft`,
        description: `It goes live when this food's draft${
          target != null ? ` (v${target})` : ""
        } is published. Until then the table below still shows the published serving.`,
      });
    } else {
      // Clean food: staged and published in one step (FV6), minting a version.
      const published = updatedFood.food_version_number;
      toast({
        title: `Serving ${verb}`,
        description:
          published != null ? `Published as v${published}.` : "Published.",
      });
    }
  };

  const handleStageError = (error: unknown) => {
    if (isFoodConflictError(error)) {
      // Someone else moved this food's draft between our read and our write. The
      // 409 body carries the current food, so refresh from it — the next attempt
      // then sends a valid If-Match — and ask the user to re-apply.
      if (error.currentFood) {
        queryClient.setQueryData(
          queryKeys.nutrition.foodItems.detail(foodItemId),
          error.currentFood
        );
      } else {
        queryClient.invalidateQueries(
          invalidateQueries.nutritionFoodItemDetail(foodItemId)
        );
      }
      queryClient.invalidateQueries(
        invalidateQueries.nutritionFoodServingList({ food_item: foodItemId })
      );
      toast({
        title: "This food just changed",
        description:
          "Someone else updated this food's draft. We've refreshed it — reopen the serving and re-apply your change.",
        variant: "destructive",
      });
      closeDialog();
      return;
    }
    if (isFoodPreconditionRequiredError(error)) {
      toast({
        title: "Couldn't save serving",
        description:
          "The food's version token was missing from the request. Reload the page and try again.",
        variant: "destructive",
      });
      return;
    }
    if (isFoodBlockerError(error)) {
      // On a clean food the stage auto-publishes in one step (FV6), so a publish
      // blocker (DUPLICATE_FOOD_NAME / DRAFT_UNAVAILABLE) can come back here.
      const detail = error.blockers
        .map((b) => b.message)
        .filter(Boolean)
        .join(" · ");
      toast({
        title: "Couldn't publish this serving change",
        description:
          detail ||
          "This food can't be published right now. Resolve the blocker and try again.",
        variant: "destructive",
      });
      return;
    }
    toast({
      title: "Error",
      description: describeServingError(error, "Failed to save serving"),
      variant: "destructive",
    });
  };

  // Adding a serving is instant: no draft, no publish, no new food version. A new
  // serving is a new row nothing references yet, so it cannot move a plan.
  const createMutation = useMutation({
    mutationFn: (data: FoodServingPayload) =>
      nutritionAPI.foodServings.add(data),
    onSuccess: () => {
      queryClient.invalidateQueries(
        invalidateQueries.nutritionFoodItemDetail(foodItemId)
      );
      queryClient.invalidateQueries(
        invalidateQueries.nutritionFoodServingList({ food_item: foodItemId })
      );
      queryClient.invalidateQueries(invalidateQueries.nutritionFoodItems());
      toast({
        title: "Serving added",
        description: "It can be used for new entries now.",
      });
      closeDialog();
    },
    onError: (error) => {
      toast({
        title: "Couldn't add this serving",
        description: describeServingError(error, "Try again in a moment."),
        variant: "destructive",
      });
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({
      id,
      version,
      data,
    }: {
      id: number;
      version: number;
      data: Partial<FoodServingPayload>;
    }) => foodVersionAPI.stageServingUpdate(id, version, data),
    onSuccess: (updatedFood) => {
      applyStagedFood(updatedFood);
      notifyStaged(updatedFood, "updated");
      closeDialog();
    },
    onError: handleStageError,
  });

  // Switching a serving off or on is instant: no draft, no publish, no new food
  // version. The row is never deleted, and plans already using it keep it.
  const switchMutation = useMutation({
    mutationFn: ({ id, on }: { id: number; on: boolean }) =>
      on
        ? nutritionAPI.foodServings.reactivate(id)
        : nutritionAPI.foodServings.deactivate(id),
    onSuccess: (_row, { on }) => {
      queryClient.invalidateQueries(
        invalidateQueries.nutritionFoodItemDetail(foodItemId)
      );
      queryClient.invalidateQueries(
        invalidateQueries.nutritionFoodServingList({ food_item: foodItemId })
      );
      queryClient.invalidateQueries(invalidateQueries.nutritionFoodItems());
      toast({
        title: on ? "Serving switched on" : "Serving switched off",
        description: on
          ? "It can be used for new entries again."
          : "It no longer appears for new entries. Plans that already use it keep it.",
      });
    },
    onError: (error) => {
      toast({
        title: "Couldn't switch this serving",
        description: switchErrorMessage(error, "Try again in a moment."),
        variant: "destructive",
      });
    },
  });

  useEffect(() => {
    if (servingsError) {
      toast({
        title: "Error",
        description:
          (servingsError as any)?.message || "Failed to fetch food servings",
        variant: "destructive",
      });
    }
  }, [servingsError, toast]);

  const foodServings = foodServingsData?.results || [];
  // When a draft is staged and the backend previews it (FV8), show the staged
  // servings — the servings as they'd be after publish — instead of the live
  // rows. Falls back to the live list on today's backend (draft_servings absent).
  const showingStaged = hasStagedDraft && !!draftServings;
  const servingRows = showingStaged ? draftServings! : foodServings;

  const closeDialog = () => {
    setIsCreating(false);
    setEditingId(null);
    setServingUnitSearch("");
    setModifiedFields(new Set());
    form.reset({
      food_item: foodItemId,
      serving_unit: undefined,
      weight_g: "",
      multiplier: "",
      weight_unit: "g",
    });
  };

  const handleCreateNew = () => {
    setIsCreating(true);
    setEditingId(null);
    // Multiplier stays blank on create — there is no server-side default any
    // more, so a pre-filled 1.00 would be a number nobody chose.
    form.reset({
      food_item: foodItemId,
      serving_unit: undefined,
      weight_g: "",
      multiplier: "",
      weight_unit: "g",
    });
    setServingUnitSearch("");
    setModifiedFields(new Set());
  };

  // Editing a saved serving is closed from the UI on purpose (see
  // SERVING_EDIT_CLOSED_REASON): changing weight_g or the multiplier rewrites
  // what every plan already advised on this serving. The machinery below is
  // kept intact -- when the backend versions serving attributes, re-enable the
  // pencil in the actions cell and nothing else has to change.
  const handleEdit = (serving: any) => {
    setEditingId(serving.id);
    setIsCreating(false);
    form.reset({
      food_item: foodItemId,
      serving_unit: servingUnitCode(serving),
      weight_g: serving.weight_g || "",
      // Only meaningful on a per-serving food; on per-100 g foods the stored
      // value is derived and the field is read-only.
      multiplier: isPerServing ? (serving.multiplier ?? "") : "",
      weight_unit: "g",
    });
    setServingUnitSearch(servingUnitName(serving));
    setModifiedFields(new Set());
  };

  const handleFieldChange = (fieldName: string, value: any) => {
    form.setValue(fieldName as keyof FoodServingFormValues, value);
    setModifiedFields((prev) => {
      const newSet = new Set(prev);
      newSet.add(fieldName);
      return newSet;
    });
  };

  const onSubmit = async (values: FoodServingFormValues) => {
    const unitName = servingUnits?.find(
      (u) => u.code === values.serving_unit
    )?.name;
    const description = buildDescription(
      unitName,
      values.weight_g || "",
      isPerServing ? "g" : (values.weight_unit ?? "g")
    );

    const weight = values.weight_g?.trim() ?? "";
    const payload: FoodServingPayload = {
      food_item: values.food_item,
      serving_unit: values.serving_unit,
      description: description || undefined,
    };
    if (isPerServing) {
      // The multiplier is the fact here; weight is optional context and is
      // omitted rather than sent as an empty value.
      payload.multiplier = values.multiplier?.trim();
      if (weight) payload.weight_g = weight;
    } else {
      // Weight only — the backend derives the multiplier and ignores one sent.
      payload.weight_g = weight;
    }

    // Adding needs no version: it does not touch the food's draft.
    if (isCreating) {
      createMutation.mutate(payload);
      return;
    }

    if (foodVersion == null) {
      // Without the food's version we cannot build a valid If-Match; staging
      // would 428. This only happens if the food detail hasn't loaded yet.
      toast({
        title: "Still loading this food",
        description:
          "Its version hasn't loaded yet, so the serving can't be staged. Try again in a moment.",
        variant: "destructive",
      });
      return;
    }

    if (editingId) {
      updateMutation.mutate({
        id: editingId,
        version: foodVersion,
        data: payload,
      });
    }
  };

  const watchedServingUnit = form.watch("serving_unit");
  const watchedWeight = form.watch("weight_g") || "";
  // The name's unit is a choice on per-100 g foods only; a per-serving
  // supplement's portion is the label's own.
  const nameUnit: "g" | "ml" = isPerServing
    ? "g"
    : (form.watch("weight_unit") ?? "g");
  const selectedServingUnit = servingUnits?.find(
    (unit) => unit.code === watchedServingUnit
  );
  const previewDescription = buildDescription(
    selectedServingUnit?.name,
    watchedWeight,
    nameUnit
  );

  // On a per-100 g food the multiplier is server-derived, so the form renders
  // the value that came back rather than taking or computing one. A serving
  // being created has nothing to render until it saves.
  const editingServing = foodServings.find((s: any) => s.id === editingId);
  const storedMultiplier: string = editingServing?.multiplier ?? "";
  // An edited weight makes that derived multiplier stale. Say so, rather than
  // printing a figure that disagrees with the weight directly above it. On a
  // per-serving food there is nothing to go stale: the multiplier is typed,
  // not derived, and comparing it to weight / 100 would be meaningless.
  const multiplierIsStale = (() => {
    if (isPerServing || !editingServing) return false;
    const next = parseFloat(watchedWeight);
    const saved = parseFloat(editingServing.weight_g ?? "");
    if (Number.isNaN(next) && Number.isNaN(saved)) return false;
    return next !== saved;
  })();

  // A preview of the multiplier the server will set. It mirrors the server's
  // FoodServing.multiplier_for_weight: weight / 100, to four places. The weight
  // has one decimal, so that is exact, not rounded. Display only: nothing here is
  // sent, and the saved value always comes from the server.
  const previewMultiplier = previewMultiplierFor(watchedWeight);
  // Preview while creating, and while an edit has changed the weight. An edit
  // that has not touched the weight shows the stored figure.
  const showingMultiplierPreview =
    previewMultiplier !== "" && (!editingServing || multiplierIsStale);

  const isDialogOpen = isCreating || editingId !== null;
  const isPending = createMutation.isPending || updateMutation.isPending;

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div className="min-w-0">
            <CardTitle className="z-label text-muted-foreground">
              Servings
            </CardTitle>
            <p className="z-caption text-muted-foreground mt-1">
              Nutrients on {foodItemName} are stored{" "}
              {FOOD_NUTRIENT_BASIS_LABELS[nutrientBasis].toLowerCase()}
              {isPerServing
                ? " — each serving needs its own multiplier."
                : " — the multiplier is derived from the weight."}
            </p>
            {canEdit && (
              <p className="z-caption text-muted-foreground mt-1">
                Saved servings can't be edited, so advice already given keeps
                the numbers it was given with. An admin can switch a serving
                off, and on again.
              </p>
            )}
          </div>
          <Button
            type="button"
            onClick={handleCreateNew}
            size="sm"
            disabled={!canEdit}
            title={!canEdit ? lockedReason : undefined}
          >
            <Plus className="h-4 w-4 mr-2" />
            Add Serving
          </Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {!canEdit && lockedReason && (
          <Alert>
            <Info className="text-muted-foreground" />
            <AlertTitle>Servings can't be changed on this food</AlertTitle>
            <AlertDescription>{lockedReason}</AlertDescription>
          </Alert>
        )}
        {hasStagedDraft && (
          <Alert>
            <Info className="text-warning" />
            <AlertTitle>
              This food has an in-progress draft
              {draftEditor ? ` by ${draftEditor}` : ""}
              {draftTarget != null ? ` · will publish as v${draftTarget}` : ""}
            </AlertTitle>
            <AlertDescription>
              A serving you add goes live at once. It does not wait for this
              draft.{" "}
              {showingStaged
                ? "The table below shows the staged servings — how they'll look once the draft is published."
                : "Serving changes staged in the draft aren't shown in the table below until it is published."}
              {otherStagedChanges.length > 0 && (
                <>
                  {" "}
                  Publishing also releases changes staged to{" "}
                  <strong>{formatList(otherStagedChanges)}</strong>.
                </>
              )}
            </AlertDescription>
          </Alert>
        )}
        {isLoadingServings ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground/70" />
          </div>
        ) : servingRows.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground z-caption">
            {canEdit
              ? 'No food servings found. Click "Add Serving" to create one.'
              : "This food has no servings, and they can't be added here."}
          </div>
        ) : (
          <div className="space-y-2">
            {showingStaged && (
              <p className="z-caption text-muted-foreground flex items-center gap-1.5">
                <Info className="size-3.5 text-warning" aria-hidden />
                Staged servings — not yet published.
              </p>
            )}
            <div className="border rounded-lg overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="z-eyebrow text-muted-foreground">
                      Serving Unit
                    </TableHead>
                    <TableHead className="z-eyebrow text-muted-foreground">
                      Description
                    </TableHead>
                    <TableHead className="text-right z-eyebrow text-muted-foreground">
                      Weight (g)
                    </TableHead>
                    <TableHead className="text-right z-eyebrow text-muted-foreground">
                      Multiplier
                    </TableHead>
                    <TableHead className="text-center z-eyebrow text-muted-foreground">
                      Default
                    </TableHead>
                    <TableHead className="text-right z-eyebrow text-muted-foreground">
                      Actions
                    </TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {servingRows.map((serving) => {
                    // Only the admin list carries is_active; a staged preview
                    // row or a plain list row has none and counts as on.
                    const isOn =
                      "is_active" in serving
                        ? (serving as { is_active?: boolean }).is_active !==
                          false
                        : true;
                    return (
                      <TableRow
                        key={serving.id}
                        className={isOn ? undefined : "opacity-60"}
                      >
                        <TableCell className="z-body">
                          {servingUnitName(serving)}
                          {!isOn && (
                            <Badge variant="secondary" className="ml-2">
                              Off
                            </Badge>
                          )}
                        </TableCell>
                        <TableCell>{serving.description || "-"}</TableCell>
                        <TableCell className="text-right">
                          {serving.weight_g || "-"}
                        </TableCell>
                        <TableCell className="text-right">
                          {serving.multiplier}
                        </TableCell>
                        <TableCell className="text-center">
                          {serving.is_default && (
                            <Badge variant="default">Default</Badge>
                          )}
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex items-center justify-end gap-1">
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              onClick={() => handleEdit(serving)}
                              disabled
                              title={
                                canEdit
                                  ? SERVING_EDIT_CLOSED_REASON
                                  : lockedReason
                              }
                            >
                              <Pencil className="h-4 w-4" />
                            </Button>
                            {(serving as { is_locked?: boolean }).is_locked ===
                              true && (
                              <span
                                className="flex items-center pl-2"
                                title={
                                  (serving as { locked_reason?: string | null })
                                    .locked_reason ??
                                  "Built-in serving. It follows the recipe."
                                }
                              >
                                <Lock
                                  className="h-3.5 w-3.5 text-muted-foreground"
                                  aria-label="Locked"
                                />
                              </span>
                            )}
                            {isStaffAdmin &&
                              typeof serving.id === "number" &&
                              (serving as { is_locked?: boolean }).is_locked !==
                                true && (
                                <span
                                  className="flex items-center gap-2 pl-2"
                                  title={
                                    !canEdit
                                      ? lockedReason
                                      : showingStaged
                                        ? SERVING_SWITCH_DRAFT_REASON
                                        : isOn && serving.is_default
                                          ? SERVING_SWITCH_DEFAULT_REASON
                                          : isOn
                                            ? "Switch this serving off"
                                            : "Switch this serving on"
                                  }
                                >
                                  <span className="z-caption text-muted-foreground">
                                    {isOn ? "On" : "Off"}
                                  </span>
                                  <Switch
                                    checked={isOn}
                                    aria-label={`${isOn ? "Switch off" : "Switch on"} ${servingUnitName(serving)}`}
                                    disabled={
                                      !canEdit ||
                                      showingStaged ||
                                      (isOn && serving.is_default) ||
                                      switchMutation.isPending
                                    }
                                    onCheckedChange={(on) =>
                                      switchMutation.mutate({
                                        id: serving.id,
                                        on,
                                      })
                                    }
                                  />
                                </span>
                              )}
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          </div>
        )}

        <Dialog
          open={isDialogOpen}
          onOpenChange={(open) => {
            if (!open) closeDialog();
          }}
        >
          <DialogContent>
            <DialogHeader>
              <DialogTitle>
                {isCreating ? "Add New Serving" : "Edit Serving"}
              </DialogTitle>
            </DialogHeader>
            <Form {...form}>
              <form
                // This card is rendered inside the host page's own <form>
                // (Add/Update Food Entry). Radix renders the dialog through a
                // portal, so this form is not a DOM descendant — but React
                // still bubbles the submit event up the component tree, which
                // fired the host's "Review & Create" and tried to create the
                // food a second time. Stop it here.
                onSubmit={(event) => {
                  event.stopPropagation();
                  void form.handleSubmit(onSubmit)(event);
                }}
                className="space-y-4"
              >
                <FormField
                  control={form.control}
                  name="serving_unit"
                  render={({ field }) => {
                    const isModified = modifiedFields.has("serving_unit");
                    return (
                      <FormItem className="flex flex-col">
                        <FormLabel>
                          Serving Unit <RequiredMark />
                        </FormLabel>
                        {/* modal: this list opens inside the Add Serving dialog,
                            and the dialog blocks wheel scrolling outside its own
                            content, so a non-modal list only scrolled with the
                            arrow keys. As its own modal layer it scrolls itself. */}
                        <Popover
                          modal
                          open={servingUnitOpen}
                          onOpenChange={setServingUnitOpen}
                        >
                          <PopoverTrigger asChild>
                            <FormControl>
                              <Button
                                type="button"
                                variant="outline"
                                role="combobox"
                                className={cn(
                                  "w-full justify-between",
                                  !field.value && "text-muted-foreground",
                                  isModified && "border-warning border-2"
                                )}
                                disabled={
                                  isLoadingServingUnits || isCareManager
                                }
                                title={
                                  isCareManager
                                    ? "Care managers can't change the serving unit."
                                    : undefined
                                }
                              >
                                {field.value
                                  ? selectedServingUnit
                                    ? selectedServingUnit.name
                                    : "Select serving unit..."
                                  : "Select serving unit..."}
                                <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                              </Button>
                            </FormControl>
                          </PopoverTrigger>
                          <PopoverContent className="w-full p-0" align="start">
                            <Command>
                              <CommandInput
                                placeholder="Search serving units..."
                                value={servingUnitSearch}
                                onValueChange={setServingUnitSearch}
                              />
                              <CommandList>
                                <CommandEmpty>
                                  No serving units found.
                                </CommandEmpty>
                                <CommandGroup>
                                  {servingUnits
                                    ?.filter((unit) =>
                                      servingUnitSearch
                                        ? unit.name
                                            .toLowerCase()
                                            .includes(
                                              servingUnitSearch.toLowerCase()
                                            )
                                        : true
                                    )
                                    .map((unit) => (
                                      <CommandItem
                                        key={unit.code}
                                        value={unit.name}
                                        onSelect={() => {
                                          handleFieldChange(
                                            "serving_unit",
                                            unit.code
                                          );
                                          setServingUnitSearch("");
                                          setServingUnitOpen(false);
                                        }}
                                      >
                                        <Check
                                          className={cn(
                                            "mr-2 h-4 w-4",
                                            field.value === unit.code
                                              ? "opacity-100"
                                              : "opacity-0"
                                          )}
                                        />
                                        {unit.name}
                                      </CommandItem>
                                    ))}
                                </CommandGroup>
                              </CommandList>
                            </Command>
                          </PopoverContent>
                        </Popover>
                        <FormMessage />
                      </FormItem>
                    );
                  }}
                />

                <FormItem>
                  <FormLabel>Description</FormLabel>
                  <FormControl>
                    <Input
                      readOnly
                      disabled
                      value={previewDescription}
                      placeholder={SERVING_DESCRIPTION_PLACEHOLDER}
                    />
                  </FormControl>
                  <p className="z-caption text-muted-foreground">
                    {SERVING_DESCRIPTION_HELPER}
                  </p>
                </FormItem>

                {!isPerServing && (
                  <div className="flex items-center justify-between gap-2">
                    <span className="z-label">Show the name in</span>
                    <ToggleGroup
                      type="single"
                      variant="outline"
                      size="sm"
                      value={nameUnit}
                      onValueChange={(value) => {
                        if (value === "g" || value === "ml") {
                          form.setValue("weight_unit", value);
                        }
                      }}
                    >
                      <ToggleGroupItem value="g" aria-label="Grams">
                        g
                      </ToggleGroupItem>
                      <ToggleGroupItem value="ml" aria-label="Millilitres">
                        ml
                      </ToggleGroupItem>
                    </ToggleGroup>
                  </div>
                )}

                <FormField
                  control={form.control}
                  name="weight_g"
                  render={({ field }) => {
                    const isModified = modifiedFields.has("weight_g");
                    return (
                      <FormItem>
                        <FormLabel>
                          Weight (g) {!isPerServing && <RequiredMark />}
                        </FormLabel>
                        <FormControl>
                          <Input
                            type="number"
                            step="0.01"
                            placeholder={
                              isPerServing ? "Optional" : "e.g., 150.0"
                            }
                            className={cn(
                              isModified && "border-warning border-2"
                            )}
                            {...field}
                            onChange={(e) => {
                              field.onChange(e);
                              handleFieldChange("weight_g", e.target.value);
                            }}
                          />
                        </FormControl>
                        {nameUnit === "ml" && (
                          <p className="z-caption text-muted-foreground">
                            {SERVING_ML_WEIGHT_HELPER}
                          </p>
                        )}
                        {isPerServing && (
                          <p className="z-caption text-muted-foreground">
                            Optional on a per-serving food — a capsule has no
                            meaningful gram weight. Leave it blank if you don't
                            know it.
                          </p>
                        )}
                        <FormMessage />
                      </FormItem>
                    );
                  }}
                />

                {isPerServing ? (
                  <FormField
                    control={form.control}
                    name="multiplier"
                    render={({ field }) => {
                      const isModified = modifiedFields.has("multiplier");
                      return (
                        <FormItem>
                          <FormLabel>
                            Multiplier <RequiredMark />
                          </FormLabel>
                          <FormControl>
                            <Input
                              type="number"
                              step="0.0001"
                              min={SERVING_MULTIPLIER_MIN}
                              placeholder="e.g., 0.3333"
                              className={cn(
                                isModified && "border-warning border-2"
                              )}
                              {...field}
                              value={field.value ?? ""}
                              onChange={(e) => {
                                field.onChange(e);
                                handleFieldChange("multiplier", e.target.value);
                              }}
                            />
                          </FormControl>
                          <p className="z-caption text-muted-foreground">
                            How many of this food's stated servings one of this
                            unit is. Nutrients on this food are stored per
                            serving, so the multiplier is the number they get
                            scaled by — there is no default and nothing derives
                            it. If the label states values per 3 capsules, one
                            capsule is 0.3333. Up to 4 decimal places.
                          </p>
                          <FormMessage />
                        </FormItem>
                      );
                    }}
                  />
                ) : (
                  <FormItem>
                    <FormLabel>Multiplier</FormLabel>
                    <FormControl>
                      <Input
                        readOnly
                        disabled
                        value={
                          showingMultiplierPreview
                            ? previewMultiplier
                            : storedMultiplier
                        }
                        placeholder={SERVING_MULTIPLIER_PLACEHOLDER}
                      />
                    </FormControl>
                    <p className="z-caption text-muted-foreground">
                      {SERVING_MULTIPLIER_HELPER}
                      {showingMultiplierPreview
                        ? " This is a preview; it is set when you save."
                        : ""}
                    </p>
                  </FormItem>
                )}

                <DialogFooter>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={closeDialog}
                    disabled={isPending}
                  >
                    Cancel
                  </Button>
                  <Button type="submit" disabled={isPending}>
                    {isPending && (
                      <Loader2 className="h-4 w-4 animate-spin mr-2" />
                    )}
                    {isCreating ? "Create" : "Update"}
                  </Button>
                </DialogFooter>
              </form>
            </Form>
          </DialogContent>
        </Dialog>
      </CardContent>
    </Card>
  );
};

export default FoodServingsCard;
